import React from 'react';
import { Meeting } from '@/types/meeting';
import { Repeat } from 'lucide-react';

interface MeetingDetailsTabProps {
  formData: Partial<Meeting>;
  onChange: (data: Partial<Meeting>) => void;
  onShowRecurrence: () => void;
}

export function MeetingDetailsTab({ formData, onChange, onShowRecurrence }: MeetingDetailsTabProps) {
  const formatDateTimeLocal = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'startTime' || name === 'endTime') {
      onChange({ ...formData, [name]: value ? new Date(value) : undefined });
    } else {
      onChange({ ...formData, [name]: value });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Meeting Title</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Meeting Type</label>
        <select
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="PARENT_TEACHER">Parent Teacher Conference</option>
          <option value="DISCIPLINARY">Disciplinary Meeting</option>
          <option value="ACADEMIC_REVIEW">Academic Review</option>
          <option value="TUTORING">Tutoring Session</option>
          <option value="STAFF">Staff Meeting</option>
          <option value="DEPARTMENT">Department Meeting</option>
          <option value="IEP">IEP Meeting</option>
          <option value="COUNSELING">Counseling Session</option>
          <option value="CLUB">Club Meeting</option>
          <option value="ADMINISTRATIVE">Administrative Meeting</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="SCHEDULED">Scheduled</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="POSTPONED">Postponed/Rescheduled</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Start Time</label>
          <input
            type="datetime-local"
            name="startTime"
            value={formatDateTimeLocal(formData.startTime)}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">End Time</label>
          <input
            type="datetime-local"
            name="endTime"
            value={formatDateTimeLocal(formData.endTime)}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            name="isVirtual"
            checked={formData.isVirtual}
            onChange={(e) => onChange({ ...formData, isVirtual: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label className="ml-2 block text-sm text-gray-700">Virtual Meeting</label>
        </div>
      </div>

      {formData.isVirtual ? (
        <div>
          <label className="block text-sm font-medium text-gray-700">Meeting Link</label>
          <input
            type="url"
            name="meetingLink"
            value={formData.meetingLink}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="https://meet.school.edu/..."
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Room number or building name"
          />
        </div>
      )}

      <div className="flex items-center space-x-4 mt-6">
        <button
          type="button"
          onClick={onShowRecurrence}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Repeat className="w-4 h-4 mr-2" />
          {formData.recurrence ? 'Edit Recurrence' : 'Make Recurring'}
        </button>
      </div>
    </div>
  );
}